import { FC, useRef } from "react";
import { useChatStore } from "../../../stores/ChatStore";
import useAccessToken from "../../../hooks/AccessToken";
import { useIntl } from "react-intl";
import { KTSVG } from "../../../_metronic/helpers";
import Swal from "sweetalert2";
import { MentionsInput, Mention, OnChangeHandlerFunc, SuggestionDataItem, MentionsInputProps } from 'react-mentions'
import { putEditChatGroup } from "../../../api/ChatCRUD";
import { chatMode, replaceMentionsWithPhone, returnListMentions } from "../../../functions/chat";
import { useSettingsStore } from "../../../stores/SettingsStore";
import { ChatGroupMessage } from "../../../interfaces/Chat/Chat";

interface Props {
    message: ChatGroupMessage
}

const mentionStyle: MentionsInputProps["style"] = {
    control: {
        backgroundColor: '#fff',
        fontSize: "1.1rem",
        minHeight: "40px",
        maxHeight: "200px",
        overflowY: "auto",
        width: "100%",
    },

    '&multiLine': {
        control: {
            minHeight: 60,
        },
        highlighter: {
            padding: 9,
        },
        input: {
            padding: 10,
            border: "1px solid #969ca4", // abu-abu (Tailwind gray-300)
            borderRadius: "8px", // radius
        },
    },

    '&singleLine': {
        display: 'inline-block',
        width: 180,

        highlighter: {
            padding: 1,
        },
        input: {
            padding: 1,
        },
    },

    suggestions: {
        list: {
            backgroundColor: 'white',
            border: "1px solid #969ca4", // abu-abu
            borderRadius: "8px", // radius
            fontSize: 16,
            bottom: "100%",
            position: "absolute",
            left: 0,
            right: 0,
            width: "200px",
        },
        item: {
            padding: '5px 15px',
            borderBottom: '1px solid rgba(0,0,0,0.15)',
            '&focused': {
                backgroundColor: '#f3f4f6',
                borderRadius: "8px",
            },
        },
    },
};

const UpdateGroupMessageBubble: FC<Props> = ({ message }) => {
    const token = useAccessToken()
    const intl = useIntl()
    const { settings } = useSettingsStore()
    const closeModalRef = useRef<HTMLButtonElement>(null);

    const {
        listChatGroupRooms, groupID, listChatGroupParticipants, selectedChatPhone, editGroupMessage, setEditGroupMessage,
        tokenChat, groupMessage, setGroupMessage
    } = useChatStore()

    const modalRef = useRef<HTMLDivElement | null>(null);
    const groupChatId = groupID?.split('|')[0] || ''
    const chatAs = groupID?.split('|')[1] || ''

    const participants = listChatGroupRooms[groupID]?.participants.map((participant) => {
        const key = participant.phone
        return {
            name: listChatGroupParticipants[key]?.name ? listChatGroupParticipants[key].name : participant.name,
            phone: listChatGroupParticipants[key]?.phone ? listChatGroupParticipants[key].phone : participant.phone,
            color: listChatGroupParticipants[key]?.color ? listChatGroupParticipants[key].color : participant.color,
            lid: listChatGroupParticipants[key]?.lid ? listChatGroupParticipants[key].lid : participant.lid
        }
    }) ?? []

    const listParticipants: SuggestionDataItem[] = participants.filter((participant) => selectedChatPhone[0].phone !== participant.phone).map((participant) => ({ id: participant.phone, display: participant.name }))

    const handleInputChange: OnChangeHandlerFunc = (value, plainTextValue, mentions) => {
        const inputText = plainTextValue;
        setEditGroupMessage(inputText);
    };

    const handleEditChat = async (as_phone: string, messageId: string, new_message: string) => {
        if (!new_message) {
            Swal.fire({
                icon: 'error',
                title: intl.formatMessage({ id: "ALERT.TITLE.EMPTY_MESSAGE" }),
                confirmButtonText: 'Ok',
                heightAuto: false,
            });

            return
        }

        Swal.fire({
            title: 'Loading',
            html: intl.formatMessage({ id: "ALERT.TITLE.EDITING_MESSAGE" }),

            didOpen: () => {
                Swal.showLoading()
            },
        })

        const text = replaceMentionsWithPhone(new_message)
        const mentions = returnListMentions(new_message)

        try {
            const response = await putEditChatGroup(messageId, groupChatId, as_phone, text, mentions, chatMode(settings.chat_version), token, tokenChat);
            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: intl.formatMessage({ id: "ALERT.TITLE.MESSAGE_EDITED" }),
                })
                closeModalRef.current?.click();
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: error.response.data.message,
                confirmButtonText: 'Ok',
                heightAuto: false,
            });
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, editedMessage?: ChatGroupMessage) => {
        // Check if Ctrl key is pressed and the key is 'b'
        if ((e.ctrlKey && e.key.toLowerCase() === 'b') || (e.metaKey && e.key.toLowerCase() === 'b')) {
            // Prevent the default behavior of the browser
            e.preventDefault();

            // Get the selected text
            const selectedText = window.getSelection()?.toString();

            // If there is selected text, replace the selected text with '*'
            if (selectedText) {
                const updatedMessage = groupMessage.replace(selectedText, `*${selectedText}*`);
                setGroupMessage(updatedMessage);
            } else {
                // If no text is selected, insert '*' at the current cursor position
                const cursorPosition = e.currentTarget.selectionStart || 0;
                const newMessage =
                    groupMessage.slice(0, cursorPosition) + '**' + groupMessage.slice(cursorPosition);

                setGroupMessage(newMessage);

                // Adjust the cursor position after inserting '*'
                e.currentTarget.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
            }
        } else if ((e.ctrlKey && e.key.toLowerCase() === 'i') || (e.metaKey && e.key.toLowerCase() === 'i')) {
            // Prevent the default behavior of the browser
            e.preventDefault();

            // Get the selected text
            const selectedText = window.getSelection()?.toString();

            // If there is selected text, replace the selected text with '_'
            if (selectedText) {
                const updatedMessage = groupMessage.replace(selectedText, `_${selectedText}_`);
                setGroupMessage(updatedMessage);
            } else {
                // If no text is selected, insert '_' at the current cursor position
                const cursorPosition = e.currentTarget.selectionStart || 0;
                const newMessage =
                    groupMessage.slice(0, cursorPosition) + '__' + groupMessage.slice(cursorPosition);

                setGroupMessage(newMessage);

                // Adjust the cursor position after inserting '_*'
                e.currentTarget.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
            }
        }

        // Call your existing onKeyDown logic if needed
        if (editedMessage) {
            onEnterPress(e, editedMessage);
        } else {
            onEnterPress(e);
        }
    };

    const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>, editedMessage?: ChatGroupMessage) => {
        if (e.keyCode === 13 && e.shiftKey === false) {
            e.preventDefault()
            if (editedMessage) {
                handleEditChat(chatAs, editedMessage.id!, editGroupMessage)
            }
        }
    }

    return (
        <div className="modal fade" tabIndex={-1} id="update-group-message-bubble-modal" ref={modalRef}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })} {intl.formatMessage({ id: 'FORM.LABEL.MESSAGE' })}</h5>
                        <div
                            className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                            data-bs-dismiss="modal"
                        >
                            <KTSVG
                                path="/media/icons/duotune/arrows/arr061.svg"
                                className="svg-icon svg-icon-2x"
                            />
                        </div>
                    </div>
                    <div className="modal-body">
                        <MentionsInput
                            placeholder="Type a message"
                            style={mentionStyle}
                            value={editGroupMessage}
                            allowSpaceInQuery={true}
                            onChange={handleInputChange}
                            onKeyDown={(e) => handleKeyDown(e as React.KeyboardEvent<HTMLTextAreaElement>, message)}
                        >
                            <Mention
                                trigger="@"
                                data={listParticipants}
                                displayTransform={(id, display) => `@${display} `}
                                style={{ fontWeight: "500", color: "#21c063", position: "relative", zIndex: 1, backgroundColor: 'white' }}
                            />
                        </MentionsInput>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-light"
                            data-bs-dismiss="modal"
                            ref={closeModalRef}
                        >
                            {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                        </button>
                        <button type="button" className={`btn btn-primary me-10`} onClick={() => handleEditChat(chatAs, message.id!, editGroupMessage)}>
                            <span className="indicator-label">
                                {intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export { UpdateGroupMessageBubble }